import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./Button";
import { getConsent, setConsent, type Consent } from "../lib/consent";
import { initMetaPixel } from "../lib/meta-pixel";

interface ConsentGateProps {
  /** Absent or empty means tracking is not configured at all: no banner, no pixel. */
  pixelId: string | undefined;
}

/**
 * Owns both the consent prompt and the pixel it gates, so there is exactly one
 * place in the codebase where tracking can start. Nothing loads until the
 * visitor has affirmatively accepted.
 */
export default function ConsentGate({ pixelId }: ConsentGateProps) {
  const [decision, setDecision] = useState<Consent | null>(() => getConsent());

  const configured = Boolean(pixelId?.trim());

  // Covers both paths into a granted state: the visitor clicking Accept now,
  // and a returning visitor who accepted on an earlier visit. initMetaPixel is
  // idempotent, so the double-invoke under StrictMode is harmless.
  useEffect(() => {
    if (decision === "granted") initMetaPixel(pixelId);
  }, [decision, pixelId]);

  function decide(consent: Consent) {
    setConsent(consent);
    setDecision(consent);
  }

  if (!configured) return null;
  if (decision !== null) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-surface-border bg-white/95 px-6 py-4 shadow-[0_-1px_3px_rgba(15,23,42,0.06)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-4xl flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-slate-600">
          We would like to use analytics cookies to understand how people find
          Intakall. Nothing is set unless you accept.{" "}
          <Link
            to="/privacy"
            className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
          >
            Privacy policy
          </Link>
        </p>

        <div className="flex shrink-0 gap-3">
          <Button variant="secondary" size="sm" onClick={() => decide("denied")}>
            Decline
          </Button>
          <Button variant="primary" size="sm" onClick={() => decide("granted")}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
